import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";
import { getHotspotAlerts, type HotspotAlert } from "@/lib/clusters.functions";
import { SEVERITY_META, type Severity } from "@/lib/reports";
import { AlertTriangle, Map, MapPin, X } from "lucide-react";
import { cn } from "@/lib/utils";

export function HotspotAlertStrip({ onMap }: { onMap?: boolean }) {
  const [alerts, setAlerts] = useState<HotspotAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try { return JSON.parse(localStorage.getItem("cp-dismissed-hotspots") ?? "[]"); } catch { return []; }
  });

  useEffect(() => {
    getHotspotAlerts().then((d) => { setAlerts(d); setLoading(false); });
  }, []);

  const dismiss = (id: string) => {
    const next = [...dismissed, id];
    setDismissed(next);
    try { localStorage.setItem("cp-dismissed-hotspots", JSON.stringify(next)); } catch {}
  };

  const visible = alerts.filter((a) => !dismissed.includes(a.id));
  if (loading) return null;
  if (visible.length === 0) return null;

  return (
    <div className={cn("space-y-2", onMap && "mb-3")}>
      {visible.map((a) => {
        const sev = SEVERITY_META[(a.severity ?? "warning") as Severity];
        return (
          <div key={a.id} className={cn(
            "flex items-start gap-3 rounded-xl border px-4 py-3",
            a.severity === "critical" || a.severity === "urgent"
              ? "border-destructive/30 bg-destructive/5"
              : "border-warning/30 bg-warning/5"
          )}>
            <AlertTriangle className={cn("mt-0.5 h-5 w-5 shrink-0", a.severity === "critical" || a.severity === "urgent" ? "text-destructive" : "text-warning")} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold">{a.title}</span>
                <Badge variant="outline" className={cn("font-normal", sev.badge)}>{sev.label}</Badge>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{a.description}</p>
              {a.location_name && (
                <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="h-3 w-3" />{a.location_name}
                </p>
              )}
              <div className="mt-2 flex items-center gap-3">
                <Link to="/map" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"><Map className="h-3.5 w-3.5" />View on map</Link>
              </div>
            </div>
            <button onClick={() => dismiss(a.id)} className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-background" aria-label="Dismiss alert">
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
